// The shape of an aggregation request, and the rules that make one well-formed.
//
// Every panel on the analytics page compiles to an `AggregateSpec`, and the
// repository has exactly one function that turns a spec into a query. That is
// what keeps per-panel SQL from accumulating: a new panel is a new spec, not a
// new query.
//
// Note what is NOT here: the clinic. Scoping is a separate parameter of
// `aggregate()`, so a spec is structurally incapable of expressing "some other
// clinic's data". This project has no row-level security, so that is the only
// guarantee available and it must have exactly one home.

import { DIMENSION_IDS, FILTERABLE_DIMENSION_IDS } from "./dimensions";
import type { DimensionId, FilterableDimensionId } from "./dimensions";
import { FACTS } from "./facts";
import type { FactId } from "./facts";
import { MEASURE_IDS } from "./measures";
import type { MeasureId } from "./measures";

export const BUCKET_GRANULARITIES = ["day", "week", "month"] as const;

export type BucketGranularity = (typeof BUCKET_GRANULARITIES)[number];

/** The dimension whose grouping needs a `bucket` to be meaningful. */
export const TIME_DIMENSION = "time_bucket" as const satisfies DimensionId;

export type DimensionFilter = {
  readonly dimension: FilterableDimensionId;
  /** Member ids to keep. An empty list is rejected — it would mean "nothing". */
  readonly in: readonly string[];
};

/**
 * Half-open: `from` is included, `toExclusive` is not.
 *
 * Named rather than merely documented, because the inclusive alternative has no
 * correct spelling. Timestamps here have microsecond resolution, so an
 * "inclusive end" computed as next-midnight-minus-one-millisecond leaves a
 * sliver of time belonging to no window at all — a row written in it would
 * appear in neither March nor April. Half-open ranges tile without gaps.
 */
export type AggregateWindow = {
  readonly from: Date;
  readonly toExclusive: Date;
};

/**
 * The widest window the analytics layer will answer, in days — five years,
 * counting both bounds.
 *
 * Here rather than on either side of the wire because both sides need the same
 * number for different reasons, and a client that disagrees with the server
 * about it produces the one failure a user cannot act on: the date picker offers
 * a range, the request is rejected, and the page shows "couldn't load" with no
 * control to move. The frontend trims a range to this and states the bound on
 * the field; the server rejects anything past it. Same number, one home.
 *
 * The size is set by what the server has to build, not by what is interesting to
 * look at: `from=1900-01-01&to=2100-12-31&bucket=day` asks for a 73,000-member
 * bucket axis (~3 MB of JSON) on top of five aggregates over two centuries. Five
 * years is beyond any real reporting question and cheap to answer.
 *
 * Deliberately *not* enforced by `validateAggregateSpec` — see its scope note.
 * This is a policy about untrusted input, so it is checked where untrusted input
 * arrives; the constant is shared, the enforcement is not.
 */
export const MAX_RANGE_DAYS = 1826;

/**
 * Ranking and truncation as one indivisible thing.
 *
 * They were once separate `orderBy` and `limit` fields, which let a caller ask
 * for a limit with no ranking — an arbitrary slice presented as a "top N", i.e.
 * a wrong answer rather than an unordered one. Fusing them makes that state
 * unrepresentable instead of merely validated.
 *
 * "Top" means descending by `measure`; there is no direction field because
 * nothing ascends. When something does, this grows a field and every existing
 * caller keeps compiling.
 */
export type TopBy = {
  readonly measure: MeasureId;
  readonly limit: number;
};

export type AggregateSpec = {
  readonly fact: FactId;
  readonly window: AggregateWindow;
  /** Required exactly when `groupBy` includes `time_bucket`. */
  readonly bucket?: BucketGranularity;
  readonly groupBy: readonly DimensionId[];
  readonly measures: readonly MeasureId[];
  readonly filters: readonly DimensionFilter[];
  /** Absent means every group is returned, in a stable key order. */
  readonly topBy?: TopBy;
};

declare const validated: unique symbol;

/**
 * A spec that has been through `validateAggregateSpec` and came back clean.
 *
 * The brand is a `unique symbol` this module never exports, so the type cannot
 * be produced anywhere but `checkAggregateSpec` below — not by a cast a caller
 * can write, and not by assembling an object literal. That is the point: the
 * repository's safety argument rests on validation having happened, and until
 * this existed it rested on a sentence in a doc comment. The first caller from
 * outside the HTTP path — a scheduled rollup, a CSV export — would have
 * typechecked, run, and handed Postgres an `IN ('')` to reject.
 */
export type ValidatedSpec = AggregateSpec & { readonly [validated]: true };

/**
 * The spec, branded, or the reasons it isn't well-formed.
 *
 * A result union rather than a throw, for the reason `validateAggregateSpec`
 * returns issues: this package is framework-agnostic and does not know what the
 * caller's error type is. `validateAggregateSpec` stays exported alongside it —
 * that is the form for "tell me everything wrong with this", which is what tests
 * and diagnostics want; this is the form for "let me run it", which is the only
 * one the repository accepts.
 */
export function checkAggregateSpec(
  spec: AggregateSpec,
):
  | { readonly ok: true; readonly spec: ValidatedSpec }
  | { readonly ok: false; readonly issues: readonly string[] } {
  const issues = validateAggregateSpec(spec);
  if (issues.length > 0) return { ok: false, issues };
  // The one cast in the arrangement, and it is immediately after the check that
  // earns it. Every other route to a `ValidatedSpec` is closed.
  return { ok: true, spec: spec as ValidatedSpec };
}

function hasDuplicates(values: readonly string[]): boolean {
  return new Set(values).size !== values.length;
}

/**
 * Return a list of reasons `spec` is not well-formed; empty means valid.
 *
 * Returns issues rather than throwing so this package stays framework-agnostic —
 * the service turns a non-empty result into the project's `ValidationError`.
 * The service is expected to call this before every `aggregate()`, so these
 * rules are enforced rather than merely documented.
 *
 * Scope: this checks that a spec is *internally coherent* — that it only names
 * things the fact has, and that its optional parts agree with each other. It is
 * deliberately not a policy gate: how far back a user may query, and whether a
 * member id is a well-formed uuid, belong to the Zod schema at the HTTP boundary
 * where the untrusted input actually arrives.
 */
export function validateAggregateSpec(spec: AggregateSpec): readonly string[] {
  const issues: string[] = [];

  // A spec naming an unknown fact can't be checked against any capability list,
  // and reporting the fact-independent problems alongside it would only bury the
  // one that has to be fixed first.
  const fact = Object.hasOwn(FACTS, spec.fact) ? FACTS[spec.fact] : undefined;
  if (!fact) return [`unknown fact "${spec.fact}"`];

  if (
    Number.isNaN(spec.window.from.getTime()) ||
    Number.isNaN(spec.window.toExclusive.getTime())
  ) {
    issues.push("window bounds must be valid dates");
  } else if (spec.window.from.getTime() > spec.window.toExclusive.getTime()) {
    issues.push("window.from must not be after window.toExclusive");
  }

  for (const dimension of spec.groupBy) {
    if (!DIMENSION_IDS.includes(dimension)) {
      issues.push(`unknown dimension "${dimension}"`);
    } else if (!fact.dimensions.includes(dimension)) {
      issues.push(`fact "${spec.fact}" cannot be grouped by "${dimension}"`);
    }
  }
  if (hasDuplicates(spec.groupBy)) {
    issues.push("groupBy must not repeat a dimension");
  }

  const groupsByTime = spec.groupBy.includes(TIME_DIMENSION);
  if (groupsByTime && spec.bucket === undefined) {
    issues.push(`grouping by "${TIME_DIMENSION}" requires a bucket`);
  }
  if (!groupsByTime && spec.bucket !== undefined) {
    issues.push(`bucket is meaningless without grouping by "${TIME_DIMENSION}"`);
  }
  if (spec.bucket !== undefined && !BUCKET_GRANULARITIES.includes(spec.bucket)) {
    issues.push(`unknown bucket "${spec.bucket}"`);
  }

  if (spec.measures.length === 0) {
    issues.push("at least one measure is required");
  }
  for (const measure of spec.measures) {
    if (!MEASURE_IDS.includes(measure)) {
      issues.push(`unknown measure "${measure}"`);
    } else if (!fact.measures.includes(measure)) {
      issues.push(`fact "${spec.fact}" has no measure "${measure}"`);
    }
  }
  if (hasDuplicates(spec.measures)) {
    issues.push("measures must not repeat");
  }

  for (const filter of spec.filters) {
    if (!FILTERABLE_DIMENSION_IDS.includes(filter.dimension)) {
      issues.push(`dimension "${filter.dimension}" is not filterable`);
    } else if (!fact.dimensions.includes(filter.dimension)) {
      issues.push(`fact "${spec.fact}" cannot be filtered by "${filter.dimension}"`);
    }
    if (filter.in.length === 0) {
      issues.push(`filter on "${filter.dimension}" must list at least one member`);
    }
    // A blank member is never a real id, and left alone it reaches the database
    // as `IN ('')` — a 500 from a function whose whole job is to produce a 400.
    if (filter.in.some((member) => member.trim() === "")) {
      issues.push(`filter on "${filter.dimension}" must not contain a blank member`);
    }
  }
  if (hasDuplicates(spec.filters.map((f) => f.dimension))) {
    issues.push("filters must not repeat a dimension");
  }

  if (spec.topBy) {
    if (!spec.measures.includes(spec.topBy.measure)) {
      issues.push(`topBy measure "${spec.topBy.measure}" is not among the selected measures`);
    }
    if (!Number.isInteger(spec.topBy.limit) || spec.topBy.limit <= 0) {
      issues.push("topBy limit must be a positive integer");
    }
  }

  return issues;
}
